import { Injectable } from '@angular/core';
import { License } from '@tangilla/tg-ng-models';
import { ItemValue, SurveyError } from 'survey-core';
import { SurveyService } from '../../../../../core/services/survey/survey.service';
import { StatesApiService } from '@tangilla/tg-ng-core';
import { ErrorsService } from '../../../../../core/services/utils/errors.service';

@Injectable({
  providedIn: 'root'
})
export class LicenseValidationValidationsService {

  constructor(private surveyService: SurveyService,
              private statesApiService: StatesApiService,
              private errorsService: ErrorsService) {
  }

  validateLicense(questionModel: any, license: License) {
    const surveyLastName = this.surveyService.surveyModel.getQuestionByName('LastName')?.value;

    if (license.license_status != 'Active') {
      this.setLicenseNotActiveError(questionModel, license);
      return false;
    } else if (questionModel.jsonObj?.inspectorsOnly && (!license.license_type.toLowerCase().includes('inspector'))) {
      this.setNotInspectorLicenseTypeError(questionModel, license);
      return false;
    } else if (questionModel.jsonObj?.brokersOnly && (!license.license_type.toLowerCase().includes('broker') && !license.license_type.toLowerCase().includes('appraiser'))) {
      this.setNotBrokerAppraiserLicenseTypeError(questionModel, license);
      return false;
    } else if (questionModel.jsonObj?.realtorsOnly && ((license.license_type.toLowerCase().includes('inspector') || license.license_type.toLowerCase().includes('corporation')))) {
      this.setNotRealtorLicenseTypeError(questionModel, license);
      return false;
    } else if (questionModel.jsonObj?.corpOnly && (!license.license_type.toLowerCase().includes('corporation'))) {
      this.setNotCorporateLicenseTypeError(questionModel, license);
      return false;
    } else if (!license.license_name.toLowerCase().includes(surveyLastName.toLowerCase()) && !questionModel.jsonObj?.corpOnly) {
      this.setLastNameDoesntMatchError(questionModel, license);
      return false;
    } else if (license.broker_last_name && !license.broker_last_name.toLowerCase().includes(surveyLastName.toLowerCase()) && questionModel.jsonObj?.corpOnly) {
      this.setBrokerLastNameDoesntMatchError(questionModel, license);
      return false;
    } else {
      if (questionModel.jsonObj?.corpOnly) {
        // this.surveyService.showNext(true);
      }

      if (this.surveyService.surveyModel.getVariable('thompson_state_old') && license.broker_relationship) { // TODO: thompson_state_old is no longer used - was for BAR implementation
        this.surveyService.surveyModel.setVariable('broker_is_realtor', license.broker_relationship.role_type === 'R' || license.broker_relationship.role_type === 'MLO');
      }

      if (this.surveyService.surveyModel.getVariable('license_affiliations_enabled')) {
        this.getStateLicenseAffiliations(questionModel, license);
      } else {
        if (this.surveyService.surveyModel.getQuestionByName('affiliated_license')) {
          this.surveyService.surveyModel.getQuestionByName('affiliated_license')['required'] = false;
        }
        if (this.surveyService.surveyModel.getPageByName('Your Affiliation')) {
          this.surveyService.surveyModel.getPageByName('Your Affiliation').visible = false;
        }
      }
      return true;
    }
  }

  private getStateLicenseAffiliations(questionModel: any, license: License) {
    const params = {};
    const stateAbbreviation = this.surveyService.surveyModel.getVariable('v-state');

    this.statesApiService.getStateLicenseNumberAffiliations(stateAbbreviation, license.license_number, {params})
      .subscribe({
        next: data => {
          this.processStateLicenseAffiliations(data, license);
        },
        error: error => {
          if (error) {
            questionModel.clearErrors();
            questionModel.addError(new SurveyError('License number not found or License number not Active.'));
          }
          this.errorsService.processError(error);
        }
      });
  }

  private processStateLicenseAffiliations(licenseAffiliations: Array<License>, license: License) {
    this.surveyService.surveyModel.setVariable('license_affiliations', licenseAffiliations);

    const affiliatedLicensesQuestion = this.surveyService.surveyModel.getQuestionByName('affiliated_licenses');
    if (licenseAffiliations && affiliatedLicensesQuestion) {
      if (licenseAffiliations.length > 0) {
        this.processAffiliations(licenseAffiliations);
      } else if (licenseAffiliations.length === 0 && ['Broker', 'Appraiser'].includes(license.license_type)) {
        this.processNoAffiliations(license);
      }
    }
  }

  processAffiliations(licenseAffiliations: Array<License>) {
    // TODO: Check this code
    for (const licenseAffiliation of licenseAffiliations) {
      const description = licenseAffiliation.broker_name + ' (' + licenseAffiliation.broker_license_number + (licenseAffiliation.corporate_license_number ? ` - ${licenseAffiliation.corporate_license_number}` : '') + ' )';
      const item = new ItemValue(licenseAffiliation.affiliated_license, description);
      this.surveyService.surveyModel.getQuestionByName('affiliated_license')['choices'].push(item);
      if (licenseAffiliations.length === 1) {
        this.surveyService.surveyModel.setValue('affiliated_license', licenseAffiliation.affiliated_license);
        this.surveyService.surveyModel.getPageByName('Your Affiliation').visible = false;
      }
    }
  }

  processNoAffiliations(license: License) {
    const licenseToUse = license.affiliated_license || license.license_number;
    const item = new ItemValue(licenseToUse, licenseToUse);
    this.surveyService.surveyModel.getQuestionByName('affiliated_license')['choices'].push(item);
    this.surveyService.surveyModel.setValue('affiliated_license', licenseToUse);
    this.surveyService.surveyModel.getPageByName('Your Affiliation').visible = false;
  }

  private setLicenseNotActiveError(questionModel: any, license: License) {
    this.setQuestionError(
      questionModel,
      'License #' + license.license_number + ' is not Active (status : ' + license.license_status + ').'
    );
  }

  private setNotInspectorLicenseTypeError(questionModel: any, license: License) {
    this.setQuestionError(
      questionModel,
      'License #' + license.license_number + ' is not an Inspectors Licence (' + license.license_type + ').'
    );
  }

  private setNotBrokerAppraiserLicenseTypeError(questionModel: any, license: License) {
    this.setQuestionError(
      questionModel,
      'License #' + license.license_number + ' is not an Broker/Appraiser Licence (' + license.license_type + ').'
    );
  }

  private setNotRealtorLicenseTypeError(questionModel: any, license: License) {
    this.setQuestionError(
      questionModel,
      'License #' + license.license_number + ' is not a valid License Type (' + license.license_type + ').'
    );
  }

  private setNotCorporateLicenseTypeError(questionModel: any, license: License) {
    this.setQuestionError(
      questionModel,
      'License #' + license.license_number + ' is not a valid License Type (' + license.license_type + ').'
    );
  }

  private setLastNameDoesntMatchError(questionModel: any, license: License) {
    this.setQuestionError(
      questionModel,
      'Last Name for License #' + license.license_number + ' does not match ' + this.surveyService.surveyModel.getQuestionByName('LastName')?.value + '.'
    );
  }

  private setBrokerLastNameDoesntMatchError(questionModel: any, license: License) {
    this.setQuestionError(
      questionModel,
      'Broker Name for Corporate License #' + license.license_number + ' does not match ' + this.surveyService.surveyModel.getQuestionByName('LastName')?.value + '.'
    );
  }

  setQuestionError(questionModel: any, errorMessage: string) {
    questionModel.clearErrors();
    questionModel.addError(new SurveyError(errorMessage));
    this.surveyService.surveyModel.setVariable('lic_valid', false);
  }
}
